import { createRoot } from "react-dom/client";
import PostalCodeFilter from "./Filter/PostalCodeFilter";
import { useContext, useEffect, useState } from "react";
import axios from "axios";
import { Stagiaire } from "./types";
import StagiaireCard from "./subcomponents/StagiaireCard";
import { AntiSpamContext, AntiSpamProvider } from "./Context";
import FiltersWrapper from "./Filter/Wrapper";
import CountryFilter from "./Filter/CountryFilter";
import { ucfirst } from './utils';
import SearchFilter from "./Filter/SearchFilter";

const container = document.querySelector('[data-id=container]');
if (!container) throw new Error('Container element not found');
const root = createRoot(container);

function Annuaire() {
    const apiUrl = import.meta.env.VITE_API_URL || 'https://localhost';

    const [postalCodeFilter, setPostalCodeFilter] = useState<string>('');
    const [countryFilter, setCountryFilter] = useState<string>('');
    const [searchFilter, setSearchFilter] = useState<string>('');

    const [stagiaires, setStagiaires] = useState<Stagiaire[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);

    const updatePostalCodeFilter = (newFilter: string) => {
        if (parseInt(newFilter) < 1 || parseInt(newFilter) > 98)
            return;
        if (!!newFilter)
            setCountryFilter('');
        setPostalCodeFilter(newFilter);
    };

    const resetFilters = () => {
        setSearchFilter('');
        setCountryFilter('');
        setPostalCodeFilter('');
    };

    const { setAntiSpamToken } = useContext(AntiSpamContext);
    const searchQuery = new URLSearchParams(window.location.search).get('search');

    useEffect(() => {
        if (searchQuery) {
            setSearchFilter(searchQuery);
        }

        const fetchAntiSpamToken = async () => {
            try {
                const response = await axios.get(`${apiUrl}/api/anti-spam-token`);
                const data = response.data;
                if (data && data.token) {
                    setAntiSpamToken(data.token);
                }
            } catch (error) {
                console.error('Erreur lors de la récupération du token anti-spam :', error);
            }
        };
        fetchAntiSpamToken();

        const fetchStagiaires = async () => {
            try {
                const response = await axios.get(`${apiUrl}/api/stagiaire`);
                const data = response.data;

                setLoading(false);
                setStagiaires(data);
                document.querySelector('[data-id="count"]')!.textContent = `Actuellement ${data.length} formateur(s) sont dans l'annuaire.`;
            } catch (error: any) {
                console.error(`Erreur lors de la récupération des données.\n${error.message}`);
                setLoading(false);
                setError(true);
            }
        };
        fetchStagiaires();
    }, []);

    let filteredStagiaires = searchFilter ?
        stagiaires.filter(s =>
            s.nomUsagePro?.toLowerCase().includes(searchFilter.toLowerCase().trim()) ||
            s.lieuxActivite.some(lieu =>
                lieu.ville?.toLowerCase().includes(searchFilter.toLowerCase().trim())
            )
        )
        : stagiaires;

    if (countryFilter) {
        filteredStagiaires =
            filteredStagiaires
                // Filter by country first - if "france" is selected, include those with no country specified as france is the default value
                .filter(s => s.lieuxActivite
                    .some(lieu => {
                        if (countryFilter.toLowerCase() === "france" && !lieu.pays)
                            return true;

                        return lieu.pays?.toLowerCase().trim().includes(countryFilter);
                    }));
    }

    if (postalCodeFilter) {
        filteredStagiaires = filteredStagiaires
            .filter(s => !postalCodeFilter ? true : s.lieuxActivite
                .some(lieu => (lieu.pays?.toLowerCase() === "france" || !lieu.pays) && lieu.codePostal?.slice(0, 2)?.includes(postalCodeFilter)));
    }

    const countriesStagiairesList = searchFilter ? filteredStagiaires : stagiaires;
    const countriesList = postalCodeFilter ?
        ["France"] :
        Array.from(new Set(countriesStagiairesList.flatMap(s => s.lieuxActivite.map(lieu => ucfirst(lieu.pays))))).sort();

    return (
        <div>
            <FiltersWrapper startOpened={searchQuery !== ''}>
                <SearchFilter filter={searchFilter} setFilter={setSearchFilter} />
                <CountryFilter filter={countryFilter} setFilter={setCountryFilter} list={countriesList} />
                {
                    (!countryFilter || countryFilter.toLowerCase() === "france") &&
                    <PostalCodeFilter filter={postalCodeFilter} setFilter={updatePostalCodeFilter} />
                }
                <a role="button" className="text-xs text-[#7196ce] italic cursor-pointer mt-2" onClick={resetFilters}>Réinitialiser</a>
            </FiltersWrapper>
            {
                error ?
                    <p className="text-informative error">Une erreur est survenue pendant le chargement. Veuillez réessayer plus tard.</p>
                    :
                    loading ?
                        <p className="text-informative loading">Chargement</p>
                        :
                        <div className="stagiaires-list">
                            {filteredStagiaires.map(s => <StagiaireCard key={s.id} stagiaire={s} />)}
                            {filteredStagiaires.length === 0 &&
                                <p className="text-informative">Aucun formateur(trice) ne correspond à ces filtres.</p>}
                        </div>
            }
        </div>
    );
}

root.render(
    <AntiSpamProvider>
        <Annuaire />
    </AntiSpamProvider>
);